import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_SETTINGS, UPDATE_TINYCMS_SETTINGS } from '../../graphql';
import { Loader } from '../common/PageLoader';
import { showInkToast } from '../cms/InkToast';
import { channelMark, channelName, Channel, REACH_CHANNELS } from '../reach/reach';
import { networkKey } from '../reach/postLength';
import {
  ACCOUNT_VOICE,
  AccountVoice,
  DIALECTS,
  PLANS_PATH,
  TRY_VOICE_CHECK,
  tryRows,
  UPDATE_ACCOUNT_VOICE,
  VoiceFinding,
} from '../reach/voiceWords';

/**
 * Account › Voice & style (backlog 1c4851c2): how everything you publish should
 * sound. It saves as you go: a field when you leave it, a choice when you make
 * it. Admins change it; everyone else reads it. Try it works for everyone, on
 * every plan, and shows what the copy edit would say.
 */

const TRY_DEBOUNCE_MS = 400;

function NetworkRow({
  channel,
  note,
  disabled,
  onSave,
}: {
  channel: Channel;
  note: string;
  disabled: boolean;
  onSave: (value: string) => void;
}) {
  const [value, setValue] = useState(note);
  useEffect(() => setValue(note), [note]);
  const name = channelName(channel.platform);
  return (
    <div className="vs-net">
      <span className="rc-mark">{channelMark(channel.platform)}</span>
      <span className="vs-net-name">{name}</span>
      <input
        className="ink-site-input"
        value={value}
        disabled={disabled}
        placeholder="Same as your voice"
        aria-label={`${name} note`}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => value !== note && onSave(value)}
      />
    </div>
  );
}

function TryIt({ accountId }: { accountId: string }) {
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ findings: VoiceFinding[]; noVoice?: boolean } | null>(
    null
  );
  const [error, setError] = useState('');
  const [check, { loading }] = useMutation(TRY_VOICE_CHECK);
  const asked = useRef(0);

  useEffect(() => {
    const body = text.trim();
    asked.current += 1;
    const mine = asked.current;
    if (!body) {
      setResult(null);
      setError('');
      return undefined;
    }
    const timer = setTimeout(() => {
      check({ variables: { accountId, text: body } })
        .then(({ data }) => {
          if (mine !== asked.current) return;
          setError('');
          setResult(data?.tryVoiceCheck || { findings: [] });
        })
        .catch((err) => {
          if (mine !== asked.current) return;
          setResult(null);
          setError(err instanceof Error ? err.message : 'That didn’t work. Try again.');
        });
    }, TRY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text, accountId, check]);

  return (
    <div className="vs-card vs-card-pad">
      <textarea
        className="vs-text"
        rows={3}
        value={text}
        aria-label="Sample text"
        placeholder="Paste a post or a paragraph"
        onChange={(e) => setText(e.target.value)}
      />
      {error ? <div className="vs-try-note">{error}</div> : null}
      {loading && !result ? <div className="vs-try-note">Checking…</div> : null}
      {result?.noVoice ? (
        <div className="vs-try-note">
          Nothing to check against yet. Write your voice or a style rule above.
        </div>
      ) : result?.findings.length ? (
        <div className="vs-rows">
          {tryRows(result.findings).map((row) => (
            <div className="vs-check" key={row.text}>
              <span style={{ color: row.color, fontWeight: 600 }}>{row.icon}</span>
              <span>{row.text}</span>
              <span className="vs-check-sev" style={{ color: row.color }}>
                {row.sev}
              </span>
            </div>
          ))}
        </div>
      ) : result ? (
        <div className="vs-try-ok">✓ In your voice. Nothing to fix.</div>
      ) : null}
    </div>
  );
}

export function VoiceStyleSettings() {
  const { accountId, isLoading } = useAccount();
  const { data, loading, error } = useQuery<{ accountVoice: AccountVoice }>(ACCOUNT_VOICE, {
    variables: { accountId },
    skip: !accountId,
  });
  const v = data?.accountVoice;
  const { data: channels } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId || !v?.socialChecks,
    fetchPolicy: 'cache-first',
  });
  // AccountVoice has no id for the cache to match on, so the answer is written back by hand.
  const [update] = useMutation(UPDATE_ACCOUNT_VOICE, {
    update: (cache, { data: saved }) => {
      if (!saved?.updateAccountVoice) return;
      cache.writeQuery({
        query: ACCOUNT_VOICE,
        variables: { accountId },
        data: { accountVoice: saved.updateAccountVoice },
      });
    },
  });
  const [saveArticles] = useMutation(UPDATE_TINYCMS_SETTINGS, {
    refetchQueries: [{ query: ACCOUNT_VOICE, variables: { accountId } }, GET_TINYCMS_SETTINGS],
  });

  const [tone, setTone] = useState('');
  const [rule, setRule] = useState('');
  const [word, setWord] = useState('');
  useEffect(() => {
    if (v) setTone(v.tone);
  }, [v?.tone]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || (loading && !v)) return <Loader label="Loading" className="ink-set-empty" />;
  if (!v || !accountId) {
    return <p className="ink-set-empty">{error?.message || 'Voice & style is unavailable.'}</p>;
  }

  const readOnly = !v.canManage;
  const save = async (input: Partial<AccountVoice>) => {
    try {
      await update({ variables: { accountId, input } });
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    }
  };

  const addRule = () => {
    const next = rule.trim();
    if (!next) return;
    setRule('');
    void save({ styleRules: [...v.styleRules, next] });
  };
  const addWord = () => {
    const next = word.trim();
    if (!next) return;
    setWord('');
    void save({ acceptedWords: [...v.acceptedWords, next] });
  };

  // One row per connected channel's network.
  const networks = [
    ...new Map(
      (channels?.socialProfiles || [])
        .filter((c) => c.isActive !== false)
        .map((c) => [networkKey(c.platform), c])
    ).entries(),
  ];

  // The one place these are set (backlog da4682cb). Articles and pages keep their own
  // setting on the server; posts and briefs need a plan with Social.
  const copyEdit = [
    {
      key: 'articles',
      name: 'Articles & pages',
      when: 'Before they go live',
      on: v.copyEditRequired,
      available: true,
      set: (on: boolean) =>
        saveArticles({ variables: { accountId, input: { copyEditRequired: on } } }),
    },
    {
      key: 'social',
      name: 'Social posts',
      when: 'Before they post',
      on: v.socialCopyEdit,
      available: v.socialChecks,
      set: (on: boolean) => save({ socialCopyEdit: on }),
    },
    {
      key: 'campaigns',
      name: 'Campaign briefs',
      when: 'Before the campaign is approved',
      on: v.campaignCopyEdit ?? v.socialCopyEdit,
      available: v.socialChecks,
      set: (on: boolean) => save({ campaignCopyEdit: on }),
    },
  ];
  const flip = async (set: (on: boolean) => Promise<unknown>, on: boolean) => {
    try {
      await set(on);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    }
  };

  return (
    <div className="vs">
      <h1 className="ink-h1">Voice &amp; style</h1>
      <p className="vs-lede">
        How everything you publish should sound. Your agent writes to it, and the copy edit checks
        against it on articles, pages, social posts and campaigns.
        {readOnly ? ' Only an account admin can change this.' : ''}
      </p>

      <h2 className="vs-h">Voice</h2>
      <p className="vs-sub">A sentence or two, the way you’d brief a new writer.</p>
      <div className="vs-card vs-card-pad">
        <textarea
          className="vs-text"
          rows={3}
          value={tone}
          aria-label="Voice"
          disabled={readOnly}
          placeholder="Plain, direct, a little dry. Short sentences. No exclamation marks."
          onChange={(e) => setTone(e.target.value)}
          onBlur={() => tone !== v.tone && void save({ tone })}
        />
        <div className="vs-sev">
          <span>When something is off voice</span>
          <div className="ink-seg" role="group" aria-label="When something is off voice">
            {(
              [
                ['warn', 'Warn before approval'],
                ['note', 'Suggestions only'],
              ] as const
            ).map(([id, label]) => (
              <button
                type="button"
                key={id}
                disabled={readOnly}
                data-active={v.voiceSeverity === id ? 'true' : 'false'}
                aria-pressed={v.voiceSeverity === id}
                onClick={() => v.voiceSeverity !== id && void save({ voiceSeverity: id })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {v.socialChecks && networks.length ? (
        <>
          <h2 className="vs-h">By network</h2>
          <p className="vs-sub">
            Same voice, adjusted for where it’s read. Leave blank to use your voice as is.
          </p>
          <div className="vs-card">
            {networks.map(([key, channel]) => (
              <NetworkRow
                key={key}
                channel={channel}
                note={v.networkNotes?.[key] || ''}
                disabled={readOnly}
                onSave={(note) => void save({ networkNotes: { ...v.networkNotes, [key]: note } })}
              />
            ))}
          </div>
        </>
      ) : null}

      <h2 className="vs-h">Style guide</h2>
      <p className="vs-sub">
        House rules. Breaking one is an error, and holds the work wherever a copy edit is required.
      </p>
      <div className="vs-card">
        {v.styleRules.map((r) => (
          <div className="vs-rule" key={r}>
            <span>{r}</span>
            {readOnly ? null : (
              <button
                type="button"
                className="vs-remove"
                aria-label={`Remove the rule: ${r}`}
                onClick={() => void save({ styleRules: v.styleRules.filter((x) => x !== r) })}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        {readOnly ? (
          v.styleRules.length ? null : (
            <div className="vs-rule ink-muted">No rules yet.</div>
          )
        ) : (
          <div className="vs-add">
            <input
              className="ink-site-input"
              value={rule}
              aria-label="New rule"
              placeholder="Add a rule, e.g. Write “email”, not “e-mail”."
              onChange={(e) => setRule(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addRule();
                }
              }}
            />
            <button type="button" className="ink-btn" onClick={addRule}>
              Add
            </button>
          </div>
        )}
      </div>

      <h2 className="vs-h">Spelling</h2>
      <p className="vs-sub">
        The spellchecker follows this dialect and never flags the words you add here.
      </p>
      <div className="vs-card">
        <div className="vs-rule">
          <span className="vs-field">Dialect</span>
          <select
            className="ink-site-input vs-select"
            value={v.dialect}
            aria-label="Dialect"
            disabled={readOnly}
            onChange={(e) => void save({ dialect: e.target.value })}
          >
            {DIALECTS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className="vs-words">
          <span className="vs-field">Words to accept</span>
          <div className="vs-chips">
            {v.acceptedWords.map((w) => (
              <span className="vs-chip" key={w}>
                {w}
                {readOnly ? null : (
                  <button
                    type="button"
                    aria-label={`Remove ${w}`}
                    onClick={() =>
                      void save({ acceptedWords: v.acceptedWords.filter((x) => x !== w) })
                    }
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {readOnly ? null : (
              <input
                className="vs-word-in"
                value={word}
                aria-label="Add word"
                placeholder="+ word"
                onChange={(e) => setWord(e.target.value)}
                onBlur={addWord}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addWord();
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>

      <h2 className="vs-h">Try it</h2>
      <p className="vs-sub">Paste anything to see what the copy edit would say.</p>
      <TryIt accountId={accountId} />

      <h2 className="vs-h" id="copy-edit">
        Require a copy edit
      </h2>
      <p className="vs-sub">
        The copy edit checks the words before they go out. Style-guide errors can hold work. Voice
        findings are only warnings.
      </p>
      <div className="vs-card">
        {copyEdit.map((c) => (
          <div className="vs-where" key={c.key}>
            <div>
              <div className="vs-field">{c.name}</div>
              <div className="vs-where-desc">{c.when}</div>
            </div>
            {c.available ? (
              <button
                type="button"
                role="switch"
                className="vs-where-switch"
                aria-checked={c.on}
                aria-label={`Require a copy edit: ${c.name}`}
                disabled={readOnly}
                onClick={() => void flip(c.set, !c.on)}
              >
                <span className="ink-switch" data-on={c.on ? 'true' : 'false'} />
              </button>
            ) : (
              <Link className="vs-where-go" to={PLANS_PATH}>
                See plans
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
